/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { defineMessages } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import get from 'lodash/get';
import omit from 'lodash/omit';
import React from 'react';
import isNil from 'lodash/isNil';
import { Link } from 'react-router-dom';
import sortBy from 'lodash/sortBy';
import { Select, SelectItem, Link as CarbonLink } from 'carbon-addons-iot-react';
import noop from 'lodash/noop';

import {
  DEFAULT_ALERT_TABLE_VIEW,
  ROUTES,
  DEFAULT_ALERT_COLUMN_IDS,
  RESOURCE_TYPES,
  SETUP_TAB_URLS,
  SETUP_ASSET_DETAILS_TAB_URLS,
  ACCESS_CONTROLS,
} from '../constants/constants';
import {
  OnCreateWorkorderType,
  UpdateAlertsType,
  AlertColumnType,
  AlertsType,
  AlertTableRow,
} from '../types/AlertsTypes';
import SummaryDashboardLink from '../../Dashboard/components/SummaryDashboardLink/SummaryDashboardLink';
import ResourceDrilldownLink from '../../Dashboard/components/ResourceDrilldownLink/ResourceDrilldownLink';
import { AlertTableDefaultColumnNames } from '../../Dashboard/types/dashboardTypes';

import { getIconByName } from 'iconUtils';
import { FormatMessageType, SortDirection } from 'CommonTypes';
import { DomainsType } from 'KPITypes';
import { EntityTypesType, EntityTypeType } from 'EntitiesTypes';
import { formatTimestampToLocalTimestamp } from 'formatterFunctions';
import InstanceDrilldownLink from 'InstanceDrilldownLink';
import { SelectableViewColumnType, TableViewType, ViewColumnType } from 'TableTypes';

const messages = defineMessages({
  high: 'High',
  medium: 'Medium',
  mediumHigh: 'Medium High',
  low: 'Low',
  new: 'New',
  validated: 'Validated',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
  timestamp: 'Time',
  severity: 'Severity',
  name: 'Name',
  entityId: 'Device ID',
  source: 'Source',
  assetAlias: 'Device alias',
  owner: 'Owner',
  entityTypeName: 'Device type',
  filterSeverity: 'Filter severity',
  filterStatus: 'Filter status',
  filterOwner: 'Filter owner',
  domainStatus: 'Status',
  changeStatus: 'Change status',
  changeOwner: 'Assign to...',
  selectOwner: 'Select owner',
  workorder: 'Service Request',
  filterWorkorder: 'Filter service request',
  createWorkorder: 'Create service request',
  filterBySource: 'Filter source',
  filterByDeviceid: 'Filter deviceid',
  filterByDeviceAlias: 'Filter device alias',
  filterByName: 'Filter name',
});

/**
 *
 * @param columnId the column id that contains the value that matches the domain
 * @param direction
 * @param dataToSort
 * @param domainValues this assumes the domain values are already sorted in order of sequenceOrder
 * @returns
 */
export const sortDataByDomain = (
  columnId: Readonly<string>,
  direction: Readonly<SortDirection>,
  dataToSort: AlertTableRow[],
  domainValues: DomainsType = []
): AlertTableRow[] => {
  // just send dataToSort back if non-sorted
  if (direction !== SortDirection.ASC && direction !== SortDirection.DESC) {
    return dataToSort;
  }

  // first clone the dataToSort for sorting
  const sortedData = dataToSort.slice(0);
  sortedData.sort((a, b) => {
    let compare = 0;
    const aValue: Readonly<string> = a.values[columnId];
    const bValue: string = b.values[columnId];
    const aDomainIndex = domainValues.findIndex(domain => domain.value === aValue);
    const bDomainIndex = domainValues.findIndex(domain => domain.value === bValue);
    if (aDomainIndex === -1) {
      compare = 1;
    } else if (bDomainIndex === -1) {
      compare = -1;
    } else {
      compare = aDomainIndex - bDomainIndex;
    }

    return direction === SortDirection.ASC ? compare : -compare;
  });
  return sortedData;
};

/**
 * translate the out-of-the-box statuses
 */
export const determineStatusString = (
  value: Readonly<string>,
  formatMessage: FormatMessageType
): string => {
  switch (value) {
    case 'New':
      return formatMessage(messages.new);
    case 'Validated':
      return formatMessage(messages.validated);
    case 'Acknowledged':
      return formatMessage(messages.acknowledged);
    case 'Resolved':
      return formatMessage(messages.resolved);
    case 'Dismissed':
      return formatMessage(messages.dismissed);
    default:
      return value;
  }
};

export const determineSeverityString = (
  value: Readonly<string>,
  formatMessage: FormatMessageType
): string => {
  switch (value) {
    case 'High':
      return formatMessage(messages.high);
    case 'Medium':
      return formatMessage(messages.medium);
    case 'Medium High':
      return formatMessage(messages.mediumHigh);
    case 'Low':
      return formatMessage(messages.low);
    default:
      return value;
  }
};

/**
 * This helper function takes the raw alert data from the API and maps it into the Table format
 * @param alertData
 * @param formatMessage
 * @returns
 */
export const mapAlertDataToTableRows = (
  alertData: AlertsType,
  formatMessage: FormatMessageType,
  entityTypesMap: Record<string, EntityTypeType>
): AlertTableRow[] =>
  alertData.map(entityEvent => {
    const { alertId, status, severity, actions, resourceDto, resourceId, deviceDto } = entityEvent;

    let actionPayload: { SR?: { TICKETID: string }; ticketid?: string } = {};
    let workorderTicketId: string;
    try {
      if (actions && actions[0] && actions[0].resultJson) {
        actionPayload = JSON.parse(actions[0].resultJson);
        workorderTicketId = actionPayload.SR?.TICKETID || actionPayload.ticketid;
      }
    } catch (err) {
      console.warn(`error parsing json payload from alert: ${JSON.stringify(err)}`);
    }

    const source = deviceDto ? deviceDto.deviceIdentifier : resourceDto?.name; // used by source column
    const deviceId = deviceDto?.name; // used by device id column
    const deviceAlias = deviceDto?.alias; // used by assetAlias column
    const resourceName =
      (resourceId && entityTypesMap[resourceId]?.name) ||
      (resourceDto?.resourceId && entityTypesMap[resourceDto.resourceId]?.name); // used by entityType column

    return {
      id: alertId?.toString(),
      values: {
        timestamp: entityEvent.beginTimestamp,
        name: entityEvent.alertTypeName,
        workorder: workorderTicketId,
        entityType: resourceName,
        entityId: deviceId,
        source,
        assetAlias: deviceAlias,
        launchURL: actions && actions[0]?.launchUrl,
        severity: determineSeverityString(severity, formatMessage), // Need to use the translated strings for filtering
        domainStatus: status, // Work around needed for hierarchyLevel APIs that return "status"
        ...(deviceDto?.dimensions || {}), // Include the custom dimension column values
        ...omit(entityEvent, 'severity', 'metadata', 'actions', 'dimensions'),
      },
    };
  });

export const handleMultiSelectFilter = (columnValue: string, filterValue: string[]): boolean =>
  Array.isArray(filterValue) && !isEmpty(filterValue)
    ? filterValue.filter(value => value).includes(columnValue)
    : true;

/** Allows correct logical sorting on the Severity and Status columns */
const sortAlertsByDomain = ({
  columnId,
  direction,
  data: dataToSort,
  severityDomains,
  statusDomains,
}: {
  columnId: string;
  direction: SortDirection;
  data: AlertTableRow[];
  severityDomains: DomainsType;
  statusDomains: DomainsType;
}): AlertTableRow[] => {
  const domainToSearch = columnId === 'severity' ? severityDomains : statusDomains;
  return sortDataByDomain(columnId, direction, dataToSort, domainToSearch);
};

// Work around for hierarchy level apis that expect a 'status' field rather than a 'domainStatus' field
export const formatAlertsForHierarchyAPI = (alerts: AlertsType): AlertsType =>
  alerts.map(alert => {
    const formattedAlert = { ...alert };
    formattedAlert.status = alert.domainStatus;
    delete formattedAlert.domainStatus;
    return formattedAlert;
  });

// Temporary check to make sure we only show the drilldown in the alert table for certain resources
// TODO: Remove this when we can drill into all hierarchy instances
const shouldShowAlertDrilldown = (type: string): boolean =>
  type === RESOURCE_TYPES.DEVICE_TYPE ||
  type === RESOURCE_TYPES.DEVICE ||
  type === RESOURCE_TYPES.ASSET ||
  type === RESOURCE_TYPES.LOCATION;

/**
 * This function returns a default set of columns for the alerts table with all their standard
 * column renderers and drilldown decisions
 */
export const determineDefaultAlertTableColumns = ({
  statusDomains = [],
  availableOwners = [],
  onUpdateAlerts = noop,
  hasShortRows,
  isPreview,
  entityTypesMap = {},
  hasAlertName,
  isTableInDashboard = false,
  severityDomains = [],
  formatMessage,
  locale = 'en',
  timeZone,
  maximoServices = [],
  onCreateWorkorder = noop,
  isHierarchyDashboard,
  userId,
  accessControls,
}: {
  statusDomains: DomainsType;
  entityTypesMap: Record<string, EntityTypeType>;
  hasAlertName: boolean;
  onUpdateAlerts: UpdateAlertsType;
  hasShortRows: boolean;
  isPreview: boolean;
  availableOwners: string[];
  severityDomains: DomainsType;
  isTableInDashboard: boolean;
  formatMessage: FormatMessageType;
  locale: string;
  timeZone: string;
  maximoServices: Record<string, unknown>[];
  onCreateWorkorder: OnCreateWorkorderType;
  isHierarchyDashboard: boolean;
  userId: string;
  accessControls: Array<string>;
}): AlertColumnType[] =>
  // These are the default AlertTable columns that are common across all AlertTables
  [
    {
      id: AlertTableDefaultColumnNames.TIMESTAMP,
      name: formatMessage(messages.timestamp),
      isSortable: true,
      renderDataFunction: ({ value }: { value: string | number }): string =>
        formatTimestampToLocalTimestamp(value, locale, timeZone),
    },
    {
      id: AlertTableDefaultColumnNames.SEVERITY,
      name: formatMessage(messages.severity),
      isSortable: true,
      isFilterable: !isEmpty(severityDomains),
      renderDataFunction: ({ value }: { value: string }): React.ReactNode => {
        const matchingSeverity = find(
          severityDomains,
          eachSeverity => determineSeverityString(eachSeverity.value, formatMessage) === value
        );
        // if we can't find the severity, just return the text
        return !matchingSeverity ? (
          value
        ) : (
          <div className="event-table-severity">
            {React.createElement(getIconByName(matchingSeverity.iconName), {
              fill: matchingSeverity.iconColor,
            })}
            {determineSeverityString(value, formatMessage)}
          </div>
        );
      },
      filter: {
        isMultiselect: true,
        placeholderText: formatMessage(messages.filterSeverity),
        options: severityDomains.map(eachDomain => ({
          id: eachDomain.value,
          text: determineSeverityString(eachDomain.value, formatMessage),
        })),
        filterFunction: handleMultiSelectFilter,
      },
      sortFunction: ({
        data,
        columnId,
        direction,
      }: {
        data: AlertTableRow[];
        columnId: string;
        direction: SortDirection;
      }): AlertTableRow[] =>
        sortAlertsByDomain({ data, columnId, direction, severityDomains, statusDomains }),
    },
    ...(hasAlertName // data Item Name
      ? [
          {
            id: AlertTableDefaultColumnNames.NAME,
            name: formatMessage(messages.name),
            isSortable: true,
            filter: {
              placeholderText: formatMessage(messages.filterByName),
            },
            renderDataFunction: (props): React.ReactNode => {
              const {
                row: { resourceId },
                value,
              } = props;

              if (isPreview) {
                return value;
              }

              const entityType = entityTypesMap && entityTypesMap[resourceId];
              const entityTypeDataItem = entityType?.dataItemDto?.find(
                dataitem => dataitem.name === value
              ); // make sure the dataItem still exists

              return entityTypeDataItem ? (
                <Link
                  className="bx--link"
                  to={`${ROUTES.SETUP_HIERARCHY}/${SETUP_TAB_URLS.DEVICES}/${entityType.uuid}/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/${entityTypeDataItem.uuid}`}>
                  {value}
                </Link>
              ) : (
                value
              );
            },
          },
        ]
      : []),
    ...(!isTableInDashboard && !isHierarchyDashboard // Only for device and device type
      ? [
          {
            id: 'entityType',
            name: formatMessage(messages.entityTypeName),
            isSortable: true,
            isFilterable: true,
            renderDataFunction: (props): React.ReactNode => {
              const { row, value } = props;
              return !isPreview ? (
                <SummaryDashboardLink
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  value={value}
                  shouldShowInNewTab={!isTableInDashboard}
                  row={row}
                  endDateOffset="minute"
                />
              ) : (
                value
              );
            },
          },
        ]
      : []),
    {
      id: AlertTableDefaultColumnNames.SOURCE,
      name: formatMessage(messages.source),
      isSortable: true,
      filter: {
        placeholderText: formatMessage(messages.filterBySource),
      },
      renderDataFunction: ({ row: { resourceDto, deviceDto } }): React.ReactNode =>
        shouldShowAlertDrilldown(resourceDto?.type) ? (
          <ResourceDrilldownLink
            uuid={resourceDto?.uuid}
            type={resourceDto?.type}
            value={deviceDto ? deviceDto.deviceIdentifier : resourceDto?.name}
            deviceTypeName={resourceDto?.name}
            deviceName={deviceDto?.name}
            userEmailId={userId}
          />
        ) : (
          (deviceDto ? deviceDto.deviceIdentifier : resourceDto?.name) || '--'
        ),
    },
    {
      id: AlertTableDefaultColumnNames.OWNER,
      name: formatMessage(messages.owner),
      isSortable: true,
      isFilterable: !isEmpty(availableOwners),
      filter: {
        isMultiselect: true,
        placeholderText: formatMessage(messages.filterOwner),
        options: sortBy(availableOwners).map(owner => ({
          id: owner,
          text: owner,
        })),
        filterFunction: handleMultiSelectFilter,
      },
      renderDataFunction: ({ value, row: { alertId } }): React.ReactNode =>
        onUpdateAlerts &&
        !isPreview &&
        accessControls.includes(ACCESS_CONTROLS.MONITOR_ALERTS_MANAGE) ? (
          <Select
            hideLabel
            id={`alert-owner-${alertId}`}
            key={`alert-owner-${alertId}`}
            inline
            size={hasShortRows ? 'sm' : undefined}
            labelText={formatMessage(messages.changeOwner)}
            value={value || 'none'}
            onChange={(event): void =>
              onUpdateAlerts([
                {
                  alertId,
                  owner: event.target.value === 'none' ? null : event.target.value,
                },
              ])
            }>
            <SelectItem
              key="alert-owner-domain-none"
              text={formatMessage(messages.selectOwner)}
              value="none"
            />
            {sortBy(availableOwners).map(owner => (
              <SelectItem
                key={`alert-owner-domain-${alertId}-${owner}`}
                text={owner}
                value={owner}
              />
            ))}
          </Select>
        ) : (
          value
        ),
    },
    {
      id: AlertTableDefaultColumnNames.STATUS,
      name: formatMessage(messages.domainStatus),
      isSortable: true,
      isFilterable: !isEmpty(statusDomains),
      filter: {
        isMultiselect: true,
        placeholderText: formatMessage(messages.filterStatus),
        options: statusDomains.map(eachDomain => ({
          id: eachDomain.value,
          text: determineStatusString(eachDomain.value, formatMessage),
        })),
        filterFunction: handleMultiSelectFilter,
      },
      renderDataFunction: ({ value, row: { alertId } }): string | JSX.Element =>
        onUpdateAlerts &&
        !isPreview &&
        accessControls.includes(ACCESS_CONTROLS.MONITOR_ALERTS_MANAGE) ? (
          <Select
            hideLabel
            id={`alert-status-${alertId}`}
            key={`alert-status-${alertId}`}
            inline
            size={hasShortRows ? 'sm' : undefined}
            labelText={formatMessage(messages.changeStatus)}
            value={value}
            onChange={(event): void =>
              onUpdateAlerts([{ alertId, domainStatus: get(event, 'target.value') }])
            }>
            {statusDomains.map(status => (
              <SelectItem
                key={`alert-status-domain-${alertId}-${status.value}`}
                text={determineStatusString(status.value, formatMessage)}
                value={status.value}
              />
            ))}
          </Select>
        ) : (
          determineStatusString(value, formatMessage)
        ),
      sortFunction: ({
        data,
        columnId,
        direction,
      }: {
        data: AlertTableRow[];
        columnId: string;
        direction: SortDirection;
      }): AlertTableRow[] =>
        sortAlertsByDomain({ data, columnId, direction, severityDomains, statusDomains }),
    },
    ...((maximoServices.length > 0 || isPreview) && // if we have configured maximo services then show the create workorder page or if we're in preview mode
    accessControls.includes(ACCESS_CONTROLS.MONITOR_ALERTS_MANAGE)
      ? [
          // Hide in hierarchy for now
          {
            id: AlertTableDefaultColumnNames.WORKORDER,
            name: formatMessage(messages.workorder),
            isSortable: true,
            isFilterable: true,
            filter: {
              placeholderText: formatMessage(messages.filterWorkorder),
            },
            ...(!isPreview
              ? {
                  renderDataFunction: ({ value, row }): React.ReactNode =>
                    isNil(value) ? (
                      <CarbonLink
                        href="#"
                        onClick={(): void =>
                          onCreateWorkorder({
                            ...row,
                            entityTypeName: entityTypesMap[row.entityTypeId]?.name,
                          })
                        }>
                        {formatMessage(messages.createWorkorder)}
                      </CarbonLink>
                    ) : (
                      <CarbonLink href={row.launchURL} target="_blank">
                        {value}
                      </CarbonLink>
                    ),
                }
              : {}),
          },
        ]
      : []),
    // Only show if we're in a dashboard, or at the deviceType level of the data tab
    ...((!isHierarchyDashboard && !isTableInDashboard) || isTableInDashboard
      ? [
          {
            id: AlertTableDefaultColumnNames.DEVICEID,
            name: formatMessage(messages.entityId),
            isSortable: true,
            filter: {
              placeholderText: formatMessage(messages.filterByDeviceid),
            },
            renderDataFunction: (props): React.ReactNode => {
              const {
                row,
                value,
                row: { entityTypeId, deviceDto },
              } = props;

              const entityTypeName = entityTypesMap[entityTypeId]?.name;
              return entityTypeName && !isPreview ? (
                <InstanceDrilldownLink
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  shouldShowInNewTab={!isTableInDashboard}
                  row={{ ...row, entityTypeName }}
                  endDateOffset="minute"
                />
              ) : (
                deviceDto?.name || value
              );
            },
          },
          {
            id: AlertTableDefaultColumnNames.DEVICEALIAS,
            name: formatMessage(messages.assetAlias),
            isSortable: true,
            filter: {
              placeholderText: formatMessage(messages.filterByDeviceAlias),
            },
            renderDataFunction: (props): React.ReactNode => {
              const {
                row,
                value,
                row: { entityTypeId, entityId, deviceDto },
              } = props;
              const entityTypeName = entityTypesMap[entityTypeId]?.name;
              return entityTypeName && !isPreview ? (
                <InstanceDrilldownLink
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  value={entityId} // we need the actual entityId in the link
                  title={deviceDto?.alias || value} // the value shown though in the link should be the alias
                  shouldShowInNewTab={!isTableInDashboard}
                  row={{ ...row, entityTypeName }}
                  endDateOffset="minute"
                />
              ) : (
                deviceDto?.alias || value
              );
            },
          },
        ]
      : []),
  ];

/**
 * This function returns a list of columns that can be added to the Alerts Table, formatted
 * in a way that the CustomizeColumnsModal can use
 */
export const determineAvailableColumnsForAlertsTable = ({
  defaultAlertTableColumns = [],
  entityTypes = [],
}: {
  defaultAlertTableColumns: AlertColumnType[];
  entityTypes: EntityTypesType;
}): SelectableViewColumnType[] => {
  const availableColumns = [];

  // First add all the default columns
  defaultAlertTableColumns.forEach(({ id, name }) => {
    availableColumns.push({ id, columnName: name, isDefault: true });
  });
  // Now add the custom columns
  entityTypes.forEach(entityType => {
    entityType.dimensionNames?.forEach(dimension => {
      if (!availableColumns.find(({ id }) => dimension === id)) {
        // because entity types can have duplicate dimensions only add if it's not already there
        availableColumns.push({
          id: dimension,
          columnName: dimension,
          isDefault: false,
        });
      }
    });
  });
  return availableColumns;
};

/**
 * This function uses the default alert table columns to determine the default view
 * @returns
 */
export const determineDefaultViewMetadata = ({
  defaultAlertTableColumns,
}: {
  defaultAlertTableColumns: AlertColumnType[];
}): TableViewType => ({
  ...DEFAULT_ALERT_TABLE_VIEW,
  columns: defaultAlertTableColumns.map(column => ({
    id: column.id,
    columnName: column.name,
  })),
});

/**
 * This function examines the viewColumns and decides which columns should actually be shown
 * in the Alert Table, and makes sure they have the correct render functions, etc attached
 */
export const determineAlertTableColumns = ({
  viewColumns = [], // the visible viewColumns
  defaultAlertTableColumns = [],
  entityTypesMap = {},
  alerts = [],
}: {
  viewColumns: ViewColumnType[];
  defaultAlertTableColumns: AlertColumnType[];
  entityTypesMap: Record<string, EntityTypeType>;
  alerts: AlertsType;
}): AlertColumnType[] =>
  isEmpty(viewColumns) // if there are no configured view columns, just return the default columns
    ? defaultAlertTableColumns
    : viewColumns // go through each of the view columns in the correct order
        .map(({ id: columnId, columnName, hasDrilldown }) => {
          // Grab the column config from our defaultPALFormattedColumns if it is one
          const matchingColumn = defaultAlertTableColumns.find(({ id }) => id === columnId);
          return matchingColumn // for columns that match the defaults then preserve everything
            ? {
                ...matchingColumn,
                name: columnName, // preserve the edited columnName
              }
            : !DEFAULT_ALERT_COLUMN_IDS.includes(columnId)
            ? {
                // If not, it's a custom alert column
                id: columnId,
                name: columnName,
                isSortable: true,
                isFilterable: true,
                ...(hasDrilldown // only attach a link if this column has drilldown
                  ? {
                      renderDataFunction: (props): React.ReactNode => {
                        const { row } = props; // eslint-disable-line react/prop-types
                        const { entityTypeId, entityId } = row; // eslint-disable-line react/prop-types
                        const entityTypeName = entityTypesMap[entityTypeId]?.name;

                        // This is needed to get the dimension values from the right entity context
                        const matchingEntityData = alerts?.find(item => item.entityId === entityId);
                        const displayValue =
                          columnId === 'assetAlias'
                            ? matchingEntityData[columnId] // Grab the asset alias if there is one
                            : matchingEntityData?.dimensions &&
                              matchingEntityData.dimensions[columnId]; // grab the dimension value
                        return (
                          <InstanceDrilldownLink
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...props}
                            row={{ ...row, entityTypeName }}
                            shouldShowInNewTab
                            value={entityId}
                            title={displayValue || ' '}
                            endDateOffset="minute"
                          />
                        );
                      },
                    }
                  : {}),
              }
            : undefined;
        })
        .filter(i => i); // have to filter out the conditional default columns like the data item name
